import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FolderPlus, Folder, Upload, Download, FileText, Trash2, Lock } from "lucide-react";
import { api } from "@/api";
import { useAuth } from "@/auth";
import { C } from "@/theme";
import { Button, Card, Field, Checkbox, Modal, LoadingState, EmptyState, Badge } from "@/ui";
import { PageHeader } from "@/components/AppShell";

const MANAGE = ["Admin", "HOT", "Assessor"];
const ROLES = ["Admin", "HOT", "Assessor", "Tutor", "Rider"];
const fmtSize = (b) => (b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

export default function Documents() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const role = user?.role;
  const canManage = MANAGE.includes(role);
  const actor = { actor_id: user?.id, actor_name: user?.name, actor_role: role };

  const foldersQ = useQuery({ queryKey: ["doc-folders"], queryFn: () => api.get(`/doc-folders?actor_role=${role}`) });
  const [activeId, setActiveId] = useState(null);
  const active = (foldersQ.data ?? []).find((f) => f.id === activeId) ?? (foldersQ.data ?? [])[0];
  const docsQ = useQuery({ queryKey: ["documents", active?.id], queryFn: () => api.get(`/documents?folder_id=${active.id}&actor_role=${role}`), enabled: !!active });

  const [editing, setEditing] = useState(null); // folder being created/edited
  const [uploadErr, setUploadErr] = useState("");
  const fileRef = useRef(null);

  const saveFolder = useMutation({
    mutationFn: () => { const body = { name: editing.name, description: editing.description || "", allowed_roles: editing.allowed_roles || [], ...actor }; return editing.id ? api.put(`/doc-folders/${editing.id}`, body) : api.post("/doc-folders", body); },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["doc-folders"] }); setEditing(null); },
  });
  const delFolder = useMutation({ mutationFn: (id) => api.del(`/doc-folders/${id}?actor_role=${role}`), onSuccess: () => { qc.invalidateQueries({ queryKey: ["doc-folders"] }); setEditing(null); setActiveId(null); } });
  const upload = useMutation({
    mutationFn: (file) => { const fd = new FormData(); fd.append("file", file); fd.append("folder_id", active.id); fd.append("actor_id", user?.id || ""); fd.append("actor_name", user?.name || ""); fd.append("actor_role", role || ""); return api.upload("/documents", fd); },
    onSuccess: () => { setUploadErr(""); qc.invalidateQueries({ queryKey: ["documents", active?.id] }); qc.invalidateQueries({ queryKey: ["doc-folders"] }); },
    onError: (e) => setUploadErr(e.message),
  });
  const delDoc = useMutation({ mutationFn: (id) => api.del(`/documents/${id}?actor_role=${role}`), onSuccess: () => { qc.invalidateQueries({ queryKey: ["documents", active?.id] }); qc.invalidateQueries({ queryKey: ["doc-folders"] }); } });

  const toggleRole = (r) => setEditing((f) => ({ ...f, allowed_roles: (f.allowed_roles || []).includes(r) ? f.allowed_roles.filter((x) => x !== r) : [...(f.allowed_roles || []), r] }));
  const onFile = (e) => { const f = e.target.files?.[0]; if (f) upload.mutate(f); e.target.value = ""; };

  if (foldersQ.isLoading) return <div className="p-6"><LoadingState /></div>;
  if (foldersQ.isError) return <div className="p-6 max-w-5xl mx-auto"><PageHeader title="Document Management" /><EmptyState icon={<Folder size={28} color={C.muted} />} title="Unable to load documents" subtitle="Please try again shortly." testID="documents-error" /></div>;
  const folders = foldersQ.data ?? [];

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <PageHeader title="Document Management" subtitle="Shared folders and files, governed by role access"
        action={canManage ? <Button label="New Folder" testID="new-folder-btn" full={false} onClick={() => setEditing({ allowed_roles: [], name: "", description: "" })} /> : null} />

      {folders.length === 0 ? <EmptyState icon={<Folder size={28} color={C.muted} />} title="No folders available" subtitle={canManage ? "Create a folder and set who can access it." : "You do not have access to any folders yet."} testID="folders-empty" /> : (
        <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-4">
          {/* Folder list */}
          <div className="flex flex-col gap-2">
            {folders.map((f) => (
              <button key={f.id} data-testid={`folder-${f.id}`} onClick={() => setActiveId(f.id)}
                style={{ background: active?.id === f.id ? C.brandTertiary : C.surface, borderColor: active?.id === f.id ? C.brandPrimary : C.divider }}
                className="text-left p-3 rounded-xl border flex items-start gap-2.5">
                <Folder size={18} color={C.brandPrimary} className="mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p style={{ color: C.onSurface }} className="text-sm font-bold truncate">{f.name}</p>
                  <p style={{ color: C.muted }} className="text-[11px]">{f.document_count} file{f.document_count === 1 ? "" : "s"}</p>
                </div>
              </button>
            ))}
          </div>

          {/* Active folder contents */}
          <div>
            {active ? (
              <Card testID="folder-contents">
                <div className="flex items-start gap-3">
                  <div className="flex-1">
                    <p style={{ color: C.onSurface }} className="font-extrabold text-[15px]">{active.name}</p>
                    {active.description ? <p style={{ color: C.muted }} className="text-[13px] mt-0.5">{active.description}</p> : null}
                    <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                      <Lock size={12} color={C.muted} />
                      {(active.allowed_roles || []).length === 0 ? <Badge label="Staff only" variant="neutral" /> : active.allowed_roles.map((r) => <Badge key={r} label={r} variant="brand" />)}
                    </div>
                  </div>
                  {canManage ? (
                    <div className="flex items-center gap-2">
                      <button data-testid={`folder-edit-${active.id}`} onClick={() => setEditing({ ...active })} style={{ background: C.surfaceTertiary, color: C.onSurface }} className="px-3 py-1.5 rounded-lg text-xs font-extrabold">Edit</button>
                      <button data-testid="folder-upload-btn" onClick={() => fileRef.current?.click()} style={{ background: C.brandPrimary, color: "#fff" }} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-extrabold"><Upload size={14} />Upload</button>
                      <input ref={fileRef} type="file" onChange={onFile} className="hidden" data-testid="folder-file-input" />
                    </div>
                  ) : null}
                </div>
                {upload.isPending ? <p style={{ color: C.muted }} className="text-[13px] mt-2">Uploading…</p> : null}
                {uploadErr ? <p style={{ color: C.error }} className="text-[13px] mt-2 font-semibold">{uploadErr}</p> : null}

                <div className="mt-3 divide-y" style={{ borderColor: C.divider }}>
                  {docsQ.isLoading ? <LoadingState /> : (docsQ.data ?? []).length === 0 ? <p style={{ color: C.muted }} className="text-[13px] py-3">No files in this folder yet.</p> :
                    (docsQ.data ?? []).map((d) => (
                      <div key={d.id} data-testid={`doc-${d.id}`} className="flex items-center gap-3 py-3">
                        <FileText size={18} color={C.brandPrimary} />
                        <div className="flex-1 min-w-0">
                          <p style={{ color: C.onSurface }} className="text-[14px] font-bold truncate">{d.original_filename}</p>
                          <p style={{ color: C.muted }} className="text-[11px]">{fmtSize(d.size)} · {d.uploaded_by_name || "—"}</p>
                        </div>
                        <button data-testid={`doc-download-${d.id}`} onClick={() => api.download(`/documents/${d.id}/download?actor_role=${role}`, d.original_filename)} style={{ background: C.surfaceTertiary, color: C.onSurface }} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-extrabold"><Download size={14} />Download</button>
                        {canManage ? <button data-testid={`doc-delete-${d.id}`} onClick={() => delDoc.mutate(d.id)} style={{ background: C.error + "22", color: C.error }} className="px-2.5 py-1.5 rounded-lg"><Trash2 size={15} /></button> : null}
                      </div>
                    ))}
                </div>
              </Card>
            ) : null}
          </div>
        </div>
      )}

      {/* Folder create/edit modal */}
      <Modal open={!!editing} onClose={() => setEditing(null)} title={editing?.id ? "Edit folder" : "New folder"} testID="folder-modal">
        {editing ? (
          <div className="flex flex-col gap-3">
            <Field label="Folder name" value={editing.name ?? ""} onChange={(v) => setEditing((f) => ({ ...f, name: v }))} testID="folder-name" />
            <Field label="Description (optional)" value={editing.description ?? ""} onChange={(v) => setEditing((f) => ({ ...f, description: v }))} testID="folder-description" />
            <div>
              <div className="flex items-center gap-2"><FolderPlus size={15} color={C.brandPrimary} /><p style={{ color: C.onSurface }} className="text-sm font-bold">Who can access this folder?</p></div>
              <p style={{ color: C.muted }} className="text-[12px] mb-1">HOT, Assessors and Admin always have access and manage uploads. Tick extra roles below.</p>
              {ROLES.map((r) => <Checkbox key={r} label={r} checked={(editing.allowed_roles || []).includes(r)} onChange={() => toggleRole(r)} testID={`folder-role-${r}`} />)}
            </div>
            <div className="flex gap-3 mt-1">
              <Button label="Cancel" variant="secondary" testID="folder-cancel" onClick={() => setEditing(null)} />
              <Button label="Save" testID="folder-save" loading={saveFolder.isPending} disabled={!editing.name?.trim()} onClick={() => saveFolder.mutate()} />
            </div>
            {editing.id ? <button data-testid="folder-delete" onClick={() => delFolder.mutate(editing.id)} style={{ color: C.error }} className="text-[13px] font-bold mt-1">Delete this folder &amp; its files</button> : null}
          </div>
        ) : null}
      </Modal>
    </div>
  );
}
